import { useRef, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { useNavigate } from "react-router-dom";
import Wallpaper1 from "../../img/accueil/wallpaper_orange.webp";
import Arrow from "../../img/angle-right.svg";

// Images d'aperçu pour le carrousel
import ConcertPreview0 from "../../img/evenement/2MUCH/thumbs/thumb83.webp";
import ConcertPreview from "../../img/evenement/WE-LOVE-GREEN-2K25/thumbs/thumb2.webp";
import ConcertPreview1 from "../../img/evenement/ECLIPSE3/thumbs/thumb4.webp";
import ConcertPreview2 from "../../img/evenement/HDP/thumbs/thumb1.webp";
import ConcertPreview3 from "../../img/evenement/NACHAVE/thumbs/thumb3.webp";
import ConcertPreview4 from "../../img/evenement/NAVA/thumbs/thumb12.webp";
import ConcertPreview5 from "../../img/evenement/PEACOCK/thumbs/thumb13.webp";
import ShootingPreview from "../../img/shooting/thumbs/thumb14.webp";
import DronePreview from "../../img/drone/thumbs/thumb2.webp";
import ArgentiquePreview from "../../img/argentique/thumbs/thumb10.webp";
import NaturePreview from "../../img/nature/thumbs/thumb35.webp";
import ManifPreview from "../../img/manif/thumbs/thumb14.webp";

// Configuration du carrousel de façon mélangée à chaque chargement
const shuffleArray = (array: any[]) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const Home = () => {
  const navigate = useNavigate();
  
  // For animation on text
  const textRef = useRef(null);
  const carouselRef = useRef(null);
  const isInView = useInView(textRef, { once: true, margin: "-50px 0px" });
  const isCarouselInView = useInView(carouselRef, { once: true, margin: "-100px 0px" });

  // Configuration responsive du carrousel
  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 1200 },
      items: 4,
      slidesToSlide: 2
    },
    desktop: {
      breakpoint: { max: 1200, min: 768 },
      items: 3,
      slidesToSlide: 1
    },
    tablet: {
      breakpoint: { max: 768, min: 600 },
      items: 2,
      slidesToSlide: 1
    },
    mobile: {
      breakpoint: { max: 600, min: 0 },
      items: 1,
      slidesToSlide: 1
    }
  };

  // Données du carrousel
  const carouselItems = useMemo(() => shuffleArray([
    {
      id: '2much',
      title: '2Much',
      subtitle: 'Concert',
      image: ConcertPreview0,
      link: '/catalogue/evenement/2much',
    },
    {
      id: 'we-love-green-2k25',
      title: 'We Love Green 2K25',
      subtitle: 'Festival',
      image: ConcertPreview,
      link: '/catalogue/evenement/we-love-green-2k25',
    },
    {
      id: 'peacock',
      title: 'Peacock',
      subtitle: 'Festival',
      image: ConcertPreview5,
      link: '/catalogue/evenement/peacock',
      category: 'concert'
    },
    {
      id: 'eclipse3',
      title: 'Eclipse 3',
      subtitle: 'Concert',
      image: ConcertPreview1,
      link: '/catalogue/evenement/eclipse3',
      category: 'concert'
    },
    {
      id: 'nachave',
      title: 'Nachave Présente',
      subtitle: 'Concert',
      image: ConcertPreview3,
      link: '/catalogue/evenement/nachave',
      category: 'concert'
    },
    {
      id: 'nava',
      title: 'Nava',
      subtitle: 'Concert',
      image: ConcertPreview4,
      link: '/catalogue/evenement/nava',
      category: 'concert'
    },
    {
      id: 'hdp',
      title: 'Heure De Pointe',
      subtitle: 'Concert',
      image: ConcertPreview2,
      link: '/catalogue/evenement/hdp',
      category: 'concert'
    },
    {
      id: 'shooting',
      title: 'Portfolio Shooting',
      subtitle: 'Shooting',
      image: ShootingPreview,
      link: '/catalogue/shooting',
      category: 'shooting'
    },
    {
      id: 'drone',
      title: 'Portfolio Drone',
      subtitle: 'Drone',
      image: DronePreview,
      link: '/catalogue/drone',
      category: 'drone'
    },
    {
      id: 'argentique',
      title: 'Portfolio Argentique',
      subtitle: 'Argentique',
      image: ArgentiquePreview,
      link: '/catalogue/argentique',
      category: 'argentique'
    },
    {
      id: 'nature',
      title: 'Portfolio Nature',
      subtitle: 'Nature',
      image: NaturePreview,
      link: '/catalogue/nature',
      category: 'nature'
    },
    {
      id: 'manifestation',
      title: 'Portfolio Manifestation',
      subtitle: 'Manifestation',
      image: ManifPreview,
      link: '/catalogue/manifestation',
      category: 'manifestation'
    },
  ]), []);

  const handleItemClick = (link: string) => {
    navigate(link);
  };

  return (
    <main className="home">
      <div id="home-image">
        <img src={Wallpaper1} alt="Photographie Nachave écran accueil" />
      </div>

      {/* Section Carrousel */}
      <section 
        ref={carouselRef}
        className="home-carousel-section"
      >
        <div className="carousel-container">
          <h2 className="carousel-title">
            Explorez mes Galeries
          </h2>

          <div id="home-text">
            <motion.p
              ref={textRef}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              {/* TODO */}
              Laissez-vous guider à travers mes galeries et découvrez mes univers.
            </motion.p>
          </div>

          <Carousel
            responsive={responsive}
            infinite={true}
            autoPlay={true}
            autoPlaySpeed={4000}
            keyBoardControl={true}
            customTransition="transform 500ms ease-in-out"
            transitionDuration={500}
            containerClass="carousel-container-custom"
            removeArrowOnDeviceType={["tablet", "mobile"]}
            dotListClass="custom-dot-list-style"
            itemClass="carousel-item-padding"
            showDots={true}
            pauseOnHover={true}
            swipeable={true}
            draggable={true}
          >
            {carouselItems.map((item, index) => (
              <motion.div
                key={item.id}
                className="carousel-item"
                initial={{ opacity: .5, scale: 0.9 }}
                animate={isCarouselInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ 
                  duration: 0.3, 
                  ease: "easeOut", 
                  delay: 0.4 + (index * 0.1) 
                }}
                onClick={() => handleItemClick(item.link)}
                whileHover={{ 
                  scale: 1.03, 
                  transition: { duration: 0.2 } 
                }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="carousel-item-image">
                  <img src={item.image} alt={item.title} loading="lazy" />
                  <div className="carousel-item-overlay">
                    <div className="carousel-item-content">
                      <span className="carousel-item-category">{item.subtitle}</span>
                      <h3 className="carousel-item-title">{item.title}</h3>
                      <div className="carousel-item-cta">
                        <span>Découvrir</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M13.025 1l-2.847 2.828 6.176 6.176h-16.354v3.992h16.354l-6.176 6.176 2.847 2.828 10.975-11z"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </Carousel>
        </div>
      </section>

      <div className="home-contact">
        <NavLink to="/contact" id="home-contact-link"><button className="classic-button">Contact <img src={Arrow} alt="Arrow" /></button></NavLink>
      </div>
      
    </main>
  );
};

export default Home;